import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { CreditCard, FileText, ArrowRightLeft, XCircle, RotateCcw, Download, ExternalLink } from "lucide-react";
import { loadStripe } from "@stripe/stripe-js";
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from "@stripe/react-stripe-js";

const stripePromise = loadStripe(
  "pk_test_51T3NsoAnj7ptF6WYNLqwgyWRW1e3C7LskgjbOWNHmsAJwSc6k9defAzUEixDytgEK0F0kZ8P1Gj4sm1rQT7UzFas00IEqvGh8x"
);

interface CardInfo {
  brand: string;
  last4: string;
  exp_month: number;
  exp_year: number;
}

interface SubDetails {
  plan: string;
  status: string;
  cancel_at_period_end: boolean;
  current_period_end: string | null;
  trial_end: string | null;
  amount: number;
  currency: string;
  interval: string;
  card: CardInfo | null;
}

interface InvoiceItem {
  id: string;
  date: string | null;
  amount: number;
  currency: string;
  status: string;
  pdf: string | null;
  number: string | null;
}

const PLAN_LABELS: Record<string, string> = {
  plus: "Flyt Pluss",
  together: "Flyt Sammen",
};

type Tab = "overview" | "invoices";

export function ManageSubscriptionDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [details, setDetails] = useState<SubDetails | null>(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState<Tab>("overview");
  const [invoices, setInvoices] = useState<InvoiceItem[] | null>(null);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [showPaymentSetup, setShowPaymentSetup] = useState(false);

  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [showChangePlanConfirm, setShowChangePlanConfirm] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const queryClient = useQueryClient();

  async function fetchDetails() {
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("manage-subscription", {
        body: { action: "details" },
      });
      if (error) throw error;
      setDetails(data as SubDetails);
    } catch {
      toast.error("Kunne ikke hente abonnementsdetaljer");
      onOpenChange(false);
    } finally {
      setLoading(false);
    }
  }

  async function fetchInvoices() {
    setInvoicesLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("manage-subscription", {
        body: { action: "invoices" },
      });
      if (error) throw error;
      setInvoices(data.invoices as InvoiceItem[]);
    } catch {
      toast.error("Kunne ikke hente fakturaer");
    } finally {
      setInvoicesLoading(false);
    }
  }

  async function handleCancel() {
    setActionLoading(true);
    try {
      const { error } = await supabase.functions.invoke("manage-subscription", {
        body: { action: "cancel" },
      });
      if (error) throw error;
      setDetails((prev) => prev ? { ...prev, cancel_at_period_end: true } : prev);
      toast.success("Abonnementet avsluttes ved periodens slutt");
      invalidate();
    } catch {
      toast.error("Kunne ikke kansellere abonnement");
    } finally {
      setActionLoading(false);
      setShowCancelConfirm(false);
    }
  }

  async function handleReactivate() {
    setActionLoading(true);
    try {
      const { error } = await supabase.functions.invoke("manage-subscription", {
        body: { action: "reactivate" },
      });
      if (error) throw error;
      setDetails((prev) => prev ? { ...prev, cancel_at_period_end: false } : prev);
      toast.success("Abonnementet er reaktivert!");
      invalidate();
    } catch {
      toast.error("Kunne ikke reaktivere abonnement");
    } finally {
      setActionLoading(false);
    }
  }

  async function handleChangePlan() {
    if (!details) return;
    const targetPlan = details.plan === "plus" ? "together" : "plus";
    setActionLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("manage-subscription", {
        body: { action: "change-plan", plan: targetPlan },
      });
      if (error) throw error;
      setDetails((prev) =>
        prev ? { ...prev, plan: data.plan, amount: data.amount } : prev
      );
      toast.success(`${isDowngrade ? "Nedgraderte" : "Oppgraderte"} til ${PLAN_LABELS[data.plan] ?? data.plan}!`);
      invalidate();
    } catch {
      toast.error("Kunne ikke bytte plan");
    } finally {
      setActionLoading(false);
      setShowChangePlanConfirm(false);
    }
  }

  function handleUpdatePaymentMethod() {
    setShowPaymentSetup(true);
  }

  function invalidate() {
    // Block background sync from overwriting DB changes for 60s
    queryClient.setQueryData(["subscription-sync", undefined], null);
    // Also reset any user-keyed variant
    queryClient.invalidateQueries({ queryKey: ["subscription-sync"], refetchType: "none" });
    queryClient.invalidateQueries({ queryKey: ["subscription"] });
    queryClient.invalidateQueries({ queryKey: ["family-entitlement"] });
  }

  useEffect(() => {
    if (open) {
      setTab("overview");
      setInvoices(null);
      fetchDetails();
    } else {
      setDetails(null);
    }
  }, [open]);

  useEffect(() => {
    if (tab === "invoices" && !invoices && !invoicesLoading) {
      fetchInvoices();
    }
  }, [tab]);

  const endDateStr = details?.current_period_end || details?.trial_end || null;
  const periodEnd = endDateStr
    ? new Date(endDateStr).toLocaleDateString("nb-NO", { day: "numeric", month: "long", year: "numeric" })
    : null;

  const priceLabel = details
    ? `${(details.amount / 100).toFixed(0)} kr/${details.interval === "month" ? "mnd" : "år"}`
    : "";

  const otherPlan = details?.plan === "plus" ? "together" : "plus";
  const PLAN_RANK: Record<string, number> = { plus: 1, together: 2 };
  const isDowngrade = details ? (PLAN_RANK[otherPlan] ?? 0) < (PLAN_RANK[details.plan] ?? 0) : false;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Administrer abonnement</DialogTitle>
            <DialogDescription>Se og endre ditt abonnement.</DialogDescription>
          </DialogHeader>

          {/* Tabs */}
          <div className="flex gap-1 border-b border-border">
            <button
              className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors ${
                tab === "overview"
                  ? "border-primary text-foreground"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
              onClick={() => setTab("overview")}
            >
              Oversikt
            </button>
            <button
              className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors ${
                tab === "invoices"
                  ? "border-primary text-foreground"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
              onClick={() => setTab("invoices")}
            >
              Fakturaer
            </button>
          </div>

          {/* Overview tab */}
          {tab === "overview" && (
            <>
              {loading ? (
                <div className="space-y-3 py-2">
                  <Skeleton className="h-5 w-32" />
                  <Skeleton className="h-4 w-48" />
                  <Skeleton className="h-4 w-40" />
                  <Skeleton className="h-4 w-36" />
                </div>
              ) : details ? (
                <div className="space-y-5 py-2">
                  {/* Plan & status */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Plan</span>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm">
                          {PLAN_LABELS[details.plan] ?? details.plan}
                        </span>
                        <Badge variant={details.cancel_at_period_end ? "destructive" : "secondary"} className="text-xs">
                          {details.cancel_at_period_end
                            ? "Kansellert"
                            : details.status === "trialing"
                            ? "Prøveperiode"
                            : "Aktiv"}
                        </Badge>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Pris</span>
                      <span className="text-sm">{priceLabel}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">
                        {details.cancel_at_period_end ? "Tilgang til" : details.status === "trialing" ? "Prøveperiode ender" : "Neste fornyelse"}
                      </span>
                      <span className="text-sm">{periodEnd ?? "–"}</span>
                    </div>
                  </div>

                  <Separator />

                  {/* Payment method */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <CreditCard className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm font-medium">Betalingsmetode</span>
                      </div>
                    </div>
                    {details.card ? (
                      <div className="flex items-center justify-between pl-6">
                        <span className="text-sm text-muted-foreground capitalize">
                          {details.card.brand} •••• {details.card.last4}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {String(details.card.exp_month).padStart(2, "0")}/{details.card.exp_year}
                        </span>
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground pl-6">Ingen kortdetaljer tilgjengelig</p>
                    )}
                    {showPaymentSetup ? (
                      <EmbeddedPaymentSetup onComplete={() => {
                        setShowPaymentSetup(false);
                        fetchDetails();
                        toast.success("Betalingsmetode oppdatert!");
                      }} />
                    ) : (
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full mt-1"
                        onClick={handleUpdatePaymentMethod}
                      >
                        <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
                        Oppdater betalingsmetode
                      </Button>
                    )}
                  </div>

                  <Separator />

                  {/* Actions */}
                  <div className="space-y-2">
                    {/* Change plan */}
                    {!details.cancel_at_period_end && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full"
                        onClick={() => setShowChangePlanConfirm(true)}
                      >
                        <ArrowRightLeft className="h-3.5 w-3.5 mr-1.5" />
                        {isDowngrade ? "Nedgrader" : "Oppgrader"} til {PLAN_LABELS[otherPlan]}
                      </Button>
                    )}

                    {/* Cancel / Reactivate */}
                    {details.cancel_at_period_end ? (
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full"
                        onClick={handleReactivate}
                        disabled={actionLoading}
                      >
                        <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
                        {actionLoading ? "Reaktiverer…" : "Reaktiver abonnement"}
                      </Button>
                    ) : (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="w-full text-destructive hover:text-destructive"
                        onClick={() => setShowCancelConfirm(true)}
                      >
                        <XCircle className="h-3.5 w-3.5 mr-1.5" />
                        Kanseller abonnement
                      </Button>
                    )}
                  </div>

                  {details.cancel_at_period_end && (
                    <p className="text-xs text-muted-foreground text-center">
                      Du har tilgang til {periodEnd}. Etter det går du tilbake til gratisplanen.
                    </p>
                  )}
                </div>
              ) : null}
            </>
          )}

          {/* Invoices tab */}
          {tab === "invoices" && (
            <div className="py-2">
              {invoicesLoading ? (
                <div className="space-y-3">
                  {[1, 2, 3].map((i) => (
                    <Skeleton key={i} className="h-10 w-full" />
                  ))}
                </div>
              ) : invoices && invoices.length > 0 ? (
                <div className="space-y-2">
                  {invoices.map((inv) => (
                    <div
                      key={inv.id}
                      className="flex items-center justify-between py-2 px-3 rounded-md border border-border"
                    >
                      <div className="flex flex-col">
                        <span className="text-sm font-medium">
                          {inv.number ?? "Faktura"}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {inv.date
                            ? new Date(inv.date).toLocaleDateString("nb-NO", {
                                day: "numeric",
                                month: "short",
                                year: "numeric",
                              })
                            : "–"}
                        </span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-sm">
                          {(inv.amount / 100).toFixed(0)} {inv.currency.toUpperCase()}
                        </span>
                        <Badge
                          variant={inv.status === "paid" ? "secondary" : "outline"}
                          className="text-xs"
                        >
                          {inv.status === "paid" ? "Betalt" : inv.status === "draft" ? "Utkast" : inv.status ?? "–"}
                        </Badge>
                        {inv.pdf && (
                          <a
                            href={inv.pdf}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-muted-foreground hover:text-foreground"
                          >
                            <Download className="h-4 w-4" />
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-4">
                  Ingen fakturaer ennå.
                </p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Cancel confirmation */}
      <AlertDialog open={showCancelConfirm} onOpenChange={setShowCancelConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Kanseller abonnement?</AlertDialogTitle>
            <AlertDialogDescription>
              Du beholder tilgangen ut perioden ({periodEnd}), men abonnementet fornyes ikke.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={actionLoading}>Behold</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCancel}
              disabled={actionLoading}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {actionLoading ? "Kansellerer…" : "Kanseller"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Change plan confirmation */}
      <AlertDialog open={showChangePlanConfirm} onOpenChange={setShowChangePlanConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {isDowngrade ? "Nedgrader plan?" : "Oppgrader plan?"}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <p>
                  Du {isDowngrade ? "nedgraderer" : "oppgraderer"} fra {PLAN_LABELS[details?.plan ?? ""]} til{" "}
                  {PLAN_LABELS[otherPlan]}.
                  Endringen trer i kraft umiddelbart, og prisforskjellen beregnes forholdsmessig.
                </p>
                {isDowngrade ? (
                  <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 space-y-1.5">
                    <p className="text-sm font-medium text-destructive">
                      Ved nedgradering mister dere:
                    </p>
                    <ul className="text-sm text-muted-foreground list-disc pl-5 space-y-1 text-left">
                      <li>Felles foreldredashboard</li>
                      <li>Oversikt over hvem som følger opp hva</li>
                      <li>Tydelig ansvarsfordeling</li>
                      <li>Barnet møter én samlet og forutsigbar struktur</li>
                    </ul>
                  </div>
                ) : (
                  <>
                    <p className="font-medium text-foreground">Med Flyt Sammen får dere:</p>
                    <ul className="text-sm list-disc pl-5 space-y-1 text-left">
                      <li>Felles foreldredashboard</li>
                      <li>Oversikt over hvem som følger opp hva</li>
                      <li>Tydelig ansvarsfordeling</li>
                      <li>Barnet møter én samlet og forutsigbar struktur</li>
                    </ul>
                  </>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={actionLoading}>Avbryt</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleChangePlan}
              disabled={actionLoading}
              className={isDowngrade ? "bg-destructive text-destructive-foreground hover:bg-destructive/90" : ""}
            >
              {actionLoading ? "Bytter…" : `${isDowngrade ? "Nedgrader" : "Oppgrader"} til ${PLAN_LABELS[otherPlan]}`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function EmbeddedPaymentSetup({ onComplete }: { onComplete: () => void }) {
  const fetchClientSecret = useCallback(async () => {
    const { data, error } = await supabase.functions.invoke("manage-subscription", {
      body: { action: "update-payment-method", embedded: true },
    });
    if (error) throw error;
    return data.clientSecret as string;
  }, []);

  return (
    <div className="min-h-[300px] mt-2">
      <EmbeddedCheckoutProvider
        stripe={stripePromise}
        options={{ fetchClientSecret }}
      >
        <EmbeddedCheckout />
      </EmbeddedCheckoutProvider>
    </div>
  );
}
